import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, Mail, FileText, ScrollText, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import ClientTagPicker from '../ClientTagPicker';

/**
 * BD-15 — "Start engagement": make a BD Pipeline engagement from a meeting or a Zoho deal, with the
 * transcripts, debriefs and email threads the person chooses already in Document Intake.
 *
 * Only the person's own meetings and mailbox are offered. Nothing flagged as sensitive is ticked up front;
 * if they tick it anyway, the flagged sentences are removed from the copy. Whatever they tick is copied into
 * the engagement, which the rest of the firm can open, and the screen says so before they start.
 */

interface Item {
  id: string;
  kind: 'transcript' | 'notes' | 'voice' | 'debrief' | 'thread';
  meeting_id?: number;
  title: string | null;
  date: string | null;
  chars?: number;
  messages?: number;
  flagged: boolean;
  omitted_sensitive?: number;
  preselected: boolean;
}
interface Sources {
  anchor: { meeting_id: number | null; meeting_title: string | null; deal_id: string | null; deal_name: string | null; org: { id: number; name: string } | null };
  account_name: string | null;
  rate_card_account: string;
  client_tag: { id: number; name: string | null; active?: boolean } | null;
  deal_candidates: { id: string; name: string; stage: string | null }[];
  suggested_name: string;
  items: Item[];
  threads_reason: string | null;
}

const KIND_LABEL: Record<Item['kind'], string> = {
  transcript: 'Transcript', notes: 'Notes', voice: 'Voice debrief', debrief: 'Debrief', thread: 'Email thread',
};

const when = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const THREAD_NOTE: Record<string, string> = {
  not_permitted: 'Your Outlook connection does not allow reading email yet, so no email threads are offered.',
  unavailable: 'Email threads could not be loaded just now.',
};

interface Props {
  meetingId?: number | null;
  dealId?: string | null;
  onClose: () => void;
}

const StartEngagementModal: React.FC<Props> = ({ meetingId = null, dealId = null, onClose }) => {
  const navigate = useNavigate();
  const [src, setSrc] = useState<Sources | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [tagId, setTagId] = useState<number | null>(null);
  const [chosenDeal, setChosenDeal] = useState<string>('');
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams();
    if (meetingId) params.set('meeting_id', String(meetingId));
    if (dealId) params.set('deal_id', dealId);
    api.get(`/api/bd/engagement-bridge/sources?${params.toString()}`)
      .then((res) => {
        if (!active) return;
        const data: Sources = res.data;
        setSrc(data);
        setName(data.suggested_name || '');
        setTagId(data.client_tag && data.client_tag.active !== false ? data.client_tag.id : null);
        setTicked(new Set(data.items.filter((i) => i.preselected).map((i) => i.id)));
      })
      .catch((err) => { if (active) setError(err?.response?.data?.error || 'Could not load what can be pre-loaded.'); });
    return () => { active = false; };
  }, [meetingId, dealId]);

  const grouped = useMemo(() => {
    const groups: { key: string; title: string; items: Item[] }[] = [];
    for (const it of src ? src.items : []) {
      const key = it.kind === 'thread' ? 'threads' : `m${it.meeting_id}`;
      let g = groups.find((x) => x.key === key);
      if (!g) { g = { key, title: it.kind === 'thread' ? 'Your email threads' : (it.title || 'Meeting'), items: [] }; groups.push(g); }
      g.items.push(it);
    }
    return groups;
  }, [src]);

  const toggle = (id: string) => {
    setTicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const flaggedTicked = src ? src.items.filter((i) => i.flagged && ticked.has(i.id)).map((i) => i.id) : [];
  const effectiveDeal = (src && src.anchor.deal_id) || chosenDeal || null;

  const start = async () => {
    if (!src) return;
    setStarting(true);
    try {
      const res = await api.post('/api/bd/engagement-bridge/start', {
        meeting_id: meetingId || null,
        deal_id: effectiveDeal,
        name: name.trim() || undefined,
        client_tag_id: tagId,
        selections: { items: Array.from(ticked), include_flagged: flaggedTicked },
      });
      const d = res.data;
      if (d.existing) toast.success('An engagement was already started from this. Opening it.');
      else toast.success(`Engagement started: ${d.artifacts} document${d.artifacts === 1 ? '' : 's'} in Document Intake, ${d.pain_points} pain point${d.pain_points === 1 ? '' : 's'} with quotes.`);
      if (d.warnings && d.warnings.length) d.warnings.forEach((w: string) => toast(w, { icon: '⚠️' }));
      onClose();
      navigate(`/app/bd-engagements/${d.engagement.id}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not start the engagement.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Start engagement" data-testid="start-engagement-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Start engagement</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>

        {error && <p className="p-4 text-sm text-red-600" data-testid="start-engagement-error">{error}</p>}
        {!src && !error && <p className="p-6 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>}

        {src && (
          <div className="p-4 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="bd-start-name">Engagement name</label>
              <input id="bd-start-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded px-3 py-1.5 text-sm" maxLength={200} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div data-testid="start-engagement-ratecard">
                <p className="text-gray-500 text-xs uppercase tracking-wide">Rate card</p>
                <p className="font-medium text-gray-900">{src.rate_card_account}</p>
                <p className="text-xs text-gray-500">{src.rate_card_account === 'Generic' ? 'Not one of the named accounts, so it bills on the Generic rate card.' : `${src.account_name || 'This account'} bills on its own rate card.`}</p>
              </div>
              <div>
                <ClientTagPicker label="Client" value={tagId} onChange={setTagId} helpText="Drives branding and cost attribution. Taken from the account when it has one." />
              </div>
            </div>

            {!src.anchor.deal_id && src.deal_candidates.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="bd-start-deal">Zoho deal (stage changes are proposed back to it for your approval)</label>
                <select id="bd-start-deal" value={chosenDeal} onChange={(e) => setChosenDeal(e.target.value)} className="w-full border rounded px-3 py-1.5 text-sm">
                  <option value="">No deal</option>
                  {src.deal_candidates.map((d) => <option key={d.id} value={d.id}>{d.name}{d.stage ? ` · ${d.stage}` : ''}</option>)}
                </select>
              </div>
            )}
            {src.anchor.deal_id && <p className="text-sm text-gray-600" data-testid="start-engagement-deal">Zoho deal: <span className="font-medium">{src.anchor.deal_name || src.anchor.deal_id}</span>. Stage changes are proposed back to it in Approvals, never automatically.</p>}

            <div data-testid="start-engagement-items">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Pre-load into Document Intake</h3>
              <p className="text-xs text-gray-500 mb-2">Everything you tick is copied into the engagement, which anyone who can open it will see. Nothing flagged as sensitive is ticked for you.</p>
              {grouped.length === 0 && <p className="text-sm text-gray-400">Nothing has been logged for this yet. Log a transcript or notes on the meeting first.</p>}
              {grouped.map((g) => (
                <div key={g.key} className="mb-3">
                  <p className="text-xs font-semibold text-gray-600 mb-1">{g.title}</p>
                  <ul className="space-y-1">
                    {g.items.map((it) => (
                      <li key={it.id} className="flex items-start gap-2 text-sm" data-testid={`start-item-${it.id}`}>
                        <input type="checkbox" id={`si-${it.id}`} checked={ticked.has(it.id)} onChange={() => toggle(it.id)} className="mt-1" />
                        <label htmlFor={`si-${it.id}`} className="flex-1 cursor-pointer">
                          <span className="inline-flex items-center gap-1 text-gray-900">
                            {it.kind === 'thread' ? <Mail className="w-3.5 h-3.5 text-gray-400" /> : it.kind === 'debrief' ? <ScrollText className="w-3.5 h-3.5 text-gray-400" /> : <FileText className="w-3.5 h-3.5 text-gray-400" />}
                            {KIND_LABEL[it.kind]}{it.kind === 'thread' ? `: ${it.title || ''}` : ''}
                          </span>
                          <span className="text-xs text-gray-500"> {when(it.date)}{it.chars ? ` · ${it.chars.toLocaleString()} characters` : ''}{it.messages ? ` · ${it.messages} message${it.messages === 1 ? '' : 's'}` : ''}</span>
                          {it.flagged && <span className="block text-xs text-amber-700" data-testid={`start-flagged-${it.id}`}><AlertTriangle className="w-3 h-3 inline mr-1" />Contains a passage flagged as sensitive. If you include it, those sentences are removed.</span>}
                          {!!it.omitted_sensitive && <span className="block text-xs text-gray-500">{it.omitted_sensitive} flagged item{it.omitted_sensitive === 1 ? '' : 's'} left out of this debrief.</span>}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {src.threads_reason && THREAD_NOTE[src.threads_reason] && <p className="text-xs text-gray-500" data-testid="start-threads-note">{THREAD_NOTE[src.threads_reason]}</p>}
            </div>

            <p className="text-xs text-gray-500">The client's pain points from the debriefs you tick are passed to the plan with their quotes.</p>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="button" onClick={start} disabled={starting || ticked.size === 0} className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50" data-testid="start-engagement-go">
                {starting ? 'Starting…' : 'Start engagement'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartEngagementModal;
